import { useBackgroundQuery, useReadQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Divider,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { QueryErrorBoundary } from "components/QueryErrorBoundary";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { parseAuditDecimal } from "encamp-shared/src/waste/invoiceAudit/decimal";
import {
  WasteContractRateRule,
  InvoiceAuditLineFragment,
  InvoiceAuditSelectedLineQuery,
  InvoiceAuditSelectedLineQueryVariables,
} from "generated-graphql/graphql";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { ReactNode, Suspense, useId, useLayoutEffect, useRef, useState } from "react";

import { DetailFact } from "../DetailLayout";
import { formatInvoiceDate } from "../utils";
import { AuditIssues, AuditResult } from "./auditUi";
import {
  CONTRACT_RULE_LABELS,
  ContractRateField,
  formatContractRateField,
  getContractRateFields,
} from "./contractRateFields";
import { formatAuditMoney, getUnderageAmount, formatUnitRate } from "./formatAuditValues";
import { InvoiceAuditCalculationPopover } from "./InvoiceAuditCalculationPopover";
import { INVOICE_AUDIT_SELECTED_LINE } from "./queries";

type AuditLine = InvoiceAuditLineFragment;
const FACTS_GRID = {
  display: "grid",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
  gap: 2,
};

function RuleChip({ rule }: { rule: WasteContractRateRule }) {
  return (
    <StatusChip
      color="info"
      label={CONTRACT_RULE_LABELS[rule]}
      sx={{
        maxWidth: "100%",
        height: "auto",
        "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 },
      }}
    />
  );
}

function CopyableId({ label, value }: { label: string; value: string }) {
  const copyToClipboard = useCopyToClipboard();
  const alerts = useAlerts();
  return (
    <DetailFact label={label}>
      <Tooltip title="Click to copy">
        <Link
          component="button"
          type="button"
          color="inherit"
          underline="hover"
          aria-label={`Copy ${label}`}
          sx={{ font: "inherit", textAlign: "left", overflowWrap: "anywhere" }}
          onClick={async () => {
            await copyToClipboard(value);
            alerts.info("Copied to clipboard");
          }}
        >
          {value}
        </Link>
      </Tooltip>
    </DetailFact>
  );
}

function ContractRateFact({ field }: { field: ContractRateField }) {
  if (field.kind === "rule")
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          {field.label}
        </Typography>
        <Box>
          <RuleChip rule={field.value} />
        </Box>
      </Box>
    );
  if (field.kind === "id" && field.value != null)
    return <CopyableId label={field.label} value={field.value} />;
  return <DetailFact label={field.label}>{formatContractRateField(field)}</DetailFact>;
}

export function InvoiceAuditLineDrawer({
  invoiceId,
  facilityId,
  lineId,
  audit,
  onClose,
  outsideResults,
}: {
  invoiceId: string;
  facilityId?: string;
  lineId: string;
  audit: AuditLine | null | undefined;
  onClose: () => void;
  outsideResults: boolean;
}) {
  const [queryRef, { refetch }] = useBackgroundQuery(INVOICE_AUDIT_SELECTED_LINE, {
    variables: { id: invoiceId, facilityId, lineId },
    skip: audit != null,
    fetchPolicy: "cache-first",
  });
  const [retry, setRetry] = useState(0);
  const onRetry = () => {
    void refetch().catch(() => undefined);
    setRetry((value) => value + 1);
  };
  const contentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    contentRef.current?.focus({ preventScroll: true });
  }, [lineId]);
  return (
    <RightDrawerShell
      open
      onClose={onClose}
      title={
        <Typography variant="h6" component="h2" sx={{ whiteSpace: "normal" }}>
          Line audit
        </Typography>
      }
    >
      <Box
        ref={contentRef}
        role="region"
        aria-label="Invoice line audit"
        tabIndex={-1}
        sx={{ outline: "none" }}
      >
        {outsideResults && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The inspected line is outside the current page or filters.
          </Alert>
        )}
        <QueryErrorBoundary
          key={retry}
          resetKey={audit ?? queryRef}
          onRetry={onRetry}
          message="Unable to load invoice audit."
        >
          <Suspense
            fallback={
              <Stack spacing={2}>
                <Skeleton height={40} />
                <Skeleton variant="rectangular" height={200} />
              </Stack>
            }
          >
            {audit ? (
              <LineExplanation audit={audit} />
            ) : queryRef ? (
              <SelectedLine queryRef={queryRef} />
            ) : null}
          </Suspense>
        </QueryErrorBoundary>
      </Box>
    </RightDrawerShell>
  );
}

function SelectedLine({
  queryRef,
}: {
  queryRef: QueryRef<InvoiceAuditSelectedLineQuery, InvoiceAuditSelectedLineQueryVariables>;
}) {
  const { data } = useReadQuery(queryRef);
  const audit = data.wasteInvoiceChargeAuditLine;
  return audit ? (
    <LineExplanation audit={audit} />
  ) : (
    <Alert severity="info">
      The linked line is not available in this invoice's facility scope.
    </Alert>
  );
}

function AuditDisclosure({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        borderTop: 1,
        borderColor: "divider",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        id={`${id}-header`}
        aria-controls={`${id}-content`}
        sx={{ px: 0 }}
      >
        <Typography variant="subtitle2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pb: 3 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

const RULE_COMPARISON = {
  PER_UNIT: { usesQuantity: true, showUnitRate: true },
  MINIMUM: { usesQuantity: true, showUnitRate: true },
  FLAT: { usesQuantity: false, showUnitRate: true },
  BASE_PLUS_ADDITIONAL: { usesQuantity: true, showUnitRate: false },
  CASE_BY_CASE: { usesQuantity: true, showUnitRate: true },
  QUOTE: { usesQuantity: true, showUnitRate: true },
  DOCUMENTED_COST: { usesQuantity: true, showUnitRate: true },
} satisfies Record<WasteContractRateRule, { usesQuantity: boolean; showUnitRate: boolean }>;

function LineExplanation({ audit }: { audit: AuditLine }) {
  const { calculation, matchedRate, line } = audit;
  const rule = calculation?.rule ?? matchedRate?.rule;
  const showUnitRate = rule == null || RULE_COMPARISON[rule].showUnitRate;
  const usesQuantity = calculation == null || RULE_COMPARISON[calculation.rule].usesQuantity;
  const quantity = (value: string | null | undefined) =>
    value == null ? "—" : `${value} ${line.billingUnit ?? ""}`.trim();
  const unitRate = (value: string | null | undefined) => formatUnitRate(value, line.billingUnit);

  return (
    <Box sx={{ overflowWrap: "anywhere" }}>
      <LineVariance audit={audit} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {line.description ?? "Invoice line"}
          </Typography>
          <Typography variant="body2">{audit.facilityName ?? "Facility not provided"}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatInvoiceDate(line.serviceDate)}
          </Typography>
        </Stack>
        <Box sx={{ flexShrink: 0 }}>
          <AuditResult result={audit.result} />
        </Box>
      </Stack>
      <Divider sx={{ my: 3 }} />
      {audit.issues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AuditIssues issues={audit.issues} />
        </Alert>
      )}
      {audit.validationIssues.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AuditIssues issues={audit.validationIssues} />
        </Alert>
      )}
      <TableContainer>
        <Table
          size="small"
          aria-label="Invoice and expected charge comparison"
          sx={{
            tableLayout: "fixed",
            fontVariantNumeric: "tabular-nums",
            "& .MuiTableCell-root": { px: { xs: 1, sm: 2 }, py: 1.5 },
          }}
        >
          <TableHead sx={{ bgcolor: "action.hover" }}>
            <TableRow>
              <TableCell>Comparison</TableCell>
              <TableCell align="right">Expected</TableCell>
              <TableCell align="right">Invoice</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                Quantity
              </TableCell>
              <TableCell align="right">
                {usesQuantity ? quantity(calculation?.quantity) : "Not used"}
              </TableCell>
              <TableCell align="right">{quantity(line.billingQuantity)}</TableCell>
            </TableRow>
            {showUnitRate && (
              <TableRow>
                <TableCell component="th" scope="row">
                  Unit rate
                </TableCell>
                <TableCell align="right">
                  {usesQuantity ? unitRate(calculation?.unitRate) : "Not applicable"}
                </TableCell>
                <TableCell align="right">{unitRate(line.unitPrice)}</TableCell>
              </TableRow>
            )}
            <TableRow sx={{ "& .MuiTableCell-root": { fontWeight: 600 } }}>
              <TableCell component="th" scope="row">
                Charge
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={0.5}>
                  <span>
                    {formatAuditMoney("expectedBaseAmount", calculation?.expectedBaseAmount)}
                  </span>
                  {calculation && (
                    <InvoiceAuditCalculationPopover
                      calculation={calculation}
                      unit={line.billingUnit}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell align="right">{formatAuditMoney("baseAmount", line.baseAmount)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 3 }}>
        {matchedRate ? (
          <AuditDisclosure title="Matched contract rate">
            <Box sx={FACTS_GRID}>
              {getContractRateFields(matchedRate).map((field) => (
                <ContractRateFact key={field.key} field={field} />
              ))}
            </Box>
          </AuditDisclosure>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No contract rate has been resolved.
          </Typography>
        )}
        <AuditDisclosure title="Fees and source details">
          <Box sx={FACTS_GRID}>
            <DetailFact label="Billed including fees">
              {formatAuditMoney("billedAmount", line.billedAmount)}
            </DetailFact>
            <DetailFact label="Tax">{formatAuditMoney("taxAmount", line.taxAmount)}</DetailFact>
            <DetailFact label="Waste fee">
              {formatAuditMoney("wasteFeeAmount", line.wasteFeeAmount)}
            </DetailFact>
            <DetailFact label="Fuel fee">
              {formatAuditMoney("fuelFeeAmount", line.fuelFeeAmount)}
            </DetailFact>
            <DetailFact label="Environmental adjustment fee">
              {formatAuditMoney(
                "environmentalAdjustmentFeeAmount",
                line.environmentalAdjustmentFeeAmount
              )}
            </DetailFact>
            <DetailFact label="Waste code">{line.code}</DetailFact>
            <DetailFact label="Pricing key">{line.pricingKey}</DetailFact>
            {!showUnitRate && (
              <DetailFact label="Reported unit rate">{unitRate(line.unitPrice)}</DetailFact>
            )}
            <CopyableId label="Source line ID" value={line.sourceLineKey} />
          </Box>
        </AuditDisclosure>
      </Box>
    </Box>
  );
}

function LineVariance({ audit }: { audit: AuditLine }) {
  const variance = parseAuditDecimal(audit.variance);
  return (
    <>
      {variance != null && !variance.isZero() && audit.result === "RATE_MISMATCH" && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              {variance.gt(0) ? "Overage" : "Underage"}
            </Typography>
            <Typography
              variant="h5"
              color={variance.gt(0) ? "error.main" : "success.main"}
              sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
            >
              {formatAuditMoney(
                "variance",
                variance.gt(0) ? audit.variance : getUnderageAmount(audit.variance)
              )}
            </Typography>
          </Stack>
          <Divider sx={{ my: 3 }} />
        </>
      )}
    </>
  );
}
